/**
 * Funciones auxiliares para gestión del almacenamiento del navegador
 */

/**
 * Guarda un elemento en localStorage con expiración opcional
 * @param key Clave para almacenar el elemento
 * @param value Valor a almacenar
 * @param expirationInMinutes Tiempo de expiración en minutos (opcional)
 */
export const setLocalStorage = (key: string, value: any, expirationInMinutes?: number): void => {
  const item = {
    value,
    timestamp: expirationInMinutes ? new Date().getTime() + (expirationInMinutes * 60000) : null
  };
  localStorage.setItem(key, JSON.stringify(item));
};

/**
 * Obtiene un elemento de localStorage, considerando su expiración
 * @param key Clave del elemento a obtener
 * @returns El valor almacenado o null si no existe o expiró
 */
export const getLocalStorage = (key: string): any => {
  const item = localStorage.getItem(key);
  if (!item) return null;

  const parsedItem = JSON.parse(item);
  if (parsedItem.timestamp && new Date().getTime() > parsedItem.timestamp) {
    localStorage.removeItem(key);
    return null;
  }

  return parsedItem.value;
};

/**
 * Guarda un elemento en sessionStorage
 * @param key Clave para almacenar el elemento
 * @param value Valor a almacenar
 */
export const setSessionStorage = (key: string, value: any): void => {
  sessionStorage.setItem(key, JSON.stringify(value));
};

/**
 * Obtiene un elemento de sessionStorage
 * @param key Clave del elemento a obtener
 * @returns El valor almacenado o null si no existe
 */
export const getSessionStorage = (key: string): any => {
  const item = sessionStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

/**
 * Elimina un elemento de localStorage
 * @param key Clave del elemento a eliminar
 */
export const removeLocalStorage = (key: string): void => {
  localStorage.removeItem(key);
};

/**
 * Elimina un elemento de sessionStorage
 * @param key Clave del elemento a eliminar
 */
export const removeSessionStorage = (key: string): void => {
  sessionStorage.removeItem(key);
};

/**
 * Limpia todos los datos de localStorage
 */
export const clearLocalStorage = (): void => {
  localStorage.clear();
};

/**
 * Limpia todos los datos de sessionStorage
 */
export const clearSessionStorage = (): void => {
  sessionStorage.clear();
};

/**
 * Verifica si existe un elemento en localStorage
 * @param key Clave a verificar
 * @returns true si existe, false si no
 */
export const existsInLocalStorage = (key: string): boolean => {
  return localStorage.getItem(key) !== null;
};

/**
 * Verifica si existe un elemento en sessionStorage
 * @param key Clave a verificar
 * @returns true si existe, false si no
 */
export const existsInSessionStorage = (key: string): boolean => {
  return sessionStorage.getItem(key) !== null;
};

/**
 * Actualiza un elemento existente en localStorage
 * @param key Clave del elemento a actualizar
 * @param value Nuevo valor
 */
export const updateLocalStorage = (key: string, value: any): void => {
  const item = getLocalStorage(key);
  if (item) {
    const updatedItem = {
      ...JSON.parse(localStorage.getItem(key)!),
      value
    };
    localStorage.setItem(key, JSON.stringify(updatedItem));
  }
};

/**
 * Actualiza un elemento existente en sessionStorage
 * @param key Clave del elemento a actualizar
 * @param value Nuevo valor
 */
export const updateSessionStorage = (key: string, value: any): void => {
  if (existsInSessionStorage(key)) {
    setSessionStorage(key, value);
  }
};
